import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, getToken, formatApiErrorDetail } from "@/lib/api";
import { buildDocFilename } from "@/lib/filename";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  UserCog,
  Shield,
  Users,
  FileText,
  ClipboardCheck,
  MessageCircle,
  TrendingUp,
  Clock,
  Trophy,
  ExternalLink,
  Eye,
  Download,
} from "lucide-react";
import PdfViewer from "@/components/PdfViewer";

const CURRENT_YEAR = new Date().getFullYear();

const STAGE_LABELS = {
  lead_nou: "Lead Nou",
  contactat: "Contactat",
  oferta: "Ofertă",
  finalizat: "Finalizat",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "acum câteva secunde";
  if (diff < 3600) return `acum ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `acum ${Math.floor(diff / 3600)}h`;
  const days = Math.floor(diff / 86400);
  if (days < 7) return `acum ${days} ${days === 1 ? "zi" : "zile"}`;
  return d.toLocaleDateString("ro-RO");
}

export default function Reports() {
  const [year, setYear] = useState("all");
  const [data, setData] = useState({ agents: [], years: [] });
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/agents", {
        params: { year: year === "all" ? undefined : year },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const yearOptions = useMemo(() => {
    const s = new Set([CURRENT_YEAR, ...(data.years || [])]);
    return Array.from(s).sort((a, b) => b - a);
  }, [data.years]);

  const agents = data.agents.filter((a) => a.role === "user");
  const admins = data.agents.filter((a) => a.role === "admin");

  const totals = useMemo(() => {
    const t = { clients: 0, contracts: 0, pvs: 0, interactions: 0 };
    for (const a of data.agents) {
      t.clients += a.clients_count;
      t.contracts += a.contracts_count;
      t.pvs += a.pvs_count;
      t.interactions += a.interactions_count;
    }
    return t;
  }, [data.agents]);

  // Top performer among agents
  const topAgent = useMemo(() => {
    if (!agents.length) return null;
    return [...agents].sort((a, b) => {
      const scoreA = a.clients_count + a.contracts_count * 2 + a.pvs_count * 2 + a.interactions_count * 0.5;
      const scoreB = b.clients_count + b.contracts_count * 2 + b.pvs_count * 2 + b.interactions_count * 0.5;
      return scoreB - scoreA;
    })[0];
  }, [agents]);

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Performanță echipă</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Rapoarte Agenți</h1>
          <p className="text-muted-foreground mt-1">
            Activitatea fiecărui agent — clienți, contracte, PV-uri și interacțiuni înregistrate.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="label-eyebrow">Filtru an</span>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-36 h-10 bg-white" data-testid="reports-year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toți anii</SelectItem>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Totals band */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <TotalTile label="Clienți" value={totals.clients} icon={Users} />
        <TotalTile label="Contracte" value={totals.contracts} icon={FileText} />
        <TotalTile label="PV-uri" value={totals.pvs} icon={ClipboardCheck} />
        <TotalTile label="Interacțiuni" value={totals.interactions} icon={MessageCircle} />
      </section>

      {/* Agents cards */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-2xl">Agenți</h2>
          {topAgent && (
            <div className="hidden sm:flex items-center gap-2 text-sm text-gold-deep">
              <Trophy className="w-4 h-4" />
              <span>Top performer: <span className="font-semibold">{topAgent.name}</span></span>
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-muted-foreground">Se încarcă...</p>
        ) : agents.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-12 text-center bg-white">
            <UserCog className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="mt-3 font-medium">Niciun agent înregistrat</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agents.map((a) => (
              <AgentCard
                key={a.id}
                agent={a}
                isTop={topAgent?.id === a.id}
                onOpen={() => setSelectedAgent(a)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Admins section */}
      {admins.length > 0 && (
        <section>
          <h2 className="font-display font-bold text-2xl mb-4">Administratori</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {admins.map((a) => (
              <AgentCard key={a.id} agent={a} onOpen={() => setSelectedAgent(a)} />
            ))}
          </div>
        </section>
      )}

      {selectedAgent && (
        <AgentDetailModal
          agentId={selectedAgent.id}
          agentSnapshot={selectedAgent}
          year={year}
          onClose={() => setSelectedAgent(null)}
        />
      )}
    </div>
  );
}

function TotalTile({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-border rounded-md p-4">
      <div className="flex items-center justify-between">
        <p className="label-eyebrow">{label}</p>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <p className="font-display font-black text-3xl mt-2 text-gold-deep">{value}</p>
    </div>
  );
}

function AgentCard({ agent, isTop, onOpen }) {
  const total = agent.clients_count + agent.contracts_count + agent.pvs_count + agent.interactions_count;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`text-left bg-white border rounded-md p-6 lift-hover w-full ${isTop ? "border-gold shadow-[0_0_0_1px_hsl(var(--gold)_/_0.4)]" : "border-border"}`}
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-11 h-11 rounded-md flex items-center justify-center shrink-0 ${
            agent.role === "admin" ? "bg-[hsl(var(--charcoal))] text-[hsl(var(--gold))]" : "bg-gold-soft text-gold-deep"
          }`}>
            {agent.role === "admin" ? <Shield className="w-5 h-5" /> : <UserCog className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <p className="font-display font-bold text-lg truncate">{agent.name}</p>
            <p className="text-xs text-muted-foreground font-mono-crm truncate">{agent.email}</p>
          </div>
        </div>
        {isTop && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-gold-deep bg-gold-soft border border-gold rounded-sm px-2 py-0.5">
            <Trophy className="w-3 h-3" /> Top
          </span>
        )}
      </div>

      <div className="mt-5 grid grid-cols-4 gap-2">
        <Stat label="Clienți" value={agent.clients_count} icon={Users} />
        <Stat label="Contracte" value={agent.contracts_count} icon={FileText} />
        <Stat label="PV-uri" value={agent.pvs_count} icon={ClipboardCheck} />
        <Stat label="Interacț." value={agent.interactions_count} icon={MessageCircle} />
      </div>

      {/* Pipeline snapshot */}
      <div className="mt-5">
        <p className="label-eyebrow mb-2">Pipeline (lead-uri deschise de agent)</p>
        <div className="grid grid-cols-4 gap-1 h-8 rounded-sm overflow-hidden border border-border">
          {Object.entries(agent.pipeline || {}).map(([k, v]) => {
            const totalPipe = Object.values(agent.pipeline || {}).reduce((a, b) => a + b, 0) || 1;
            const pct = Math.round((v / totalPipe) * 100);
            const bg = {
              lead_nou: "#DBEAFE",
              contactat: "#FEF3C7",
              oferta: "#EDE9FE",
              finalizat: "#D1FAE5",
            }[k] || "#F1F5F9";
            const fg = {
              lead_nou: "#1E40AF",
              contactat: "#92400E",
              oferta: "#5B21B6",
              finalizat: "#065F46",
            }[k] || "#334155";
            return (
              <div
                key={k}
                className="flex items-center justify-center text-[10px] font-bold uppercase tracking-widest"
                style={{ backgroundColor: bg, color: fg }}
                title={`${STAGE_LABELS[k]}: ${v} (${pct}%)`}
              >
                {v > 0 ? v : ""}
              </div>
            );
          })}
        </div>
        <div className="mt-1 grid grid-cols-4 gap-1 text-[9px] uppercase tracking-widest text-muted-foreground text-center">
          <span>Nou</span>
          <span>Contactat</span>
          <span>Ofertă</span>
          <span>Finalizat</span>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          <span>Ultima activitate: <span className="text-foreground font-medium">{timeAgo(agent.last_activity)}</span></span>
        </div>
        <span className="flex items-center gap-1 text-gold-deep font-mono-crm">
          <TrendingUp className="w-3.5 h-3.5" /> total: {total}
        </span>
      </div>
      <p className="mt-3 text-[10px] uppercase tracking-widest text-gold-deep font-semibold flex items-center gap-1">
        Vezi detalii <ExternalLink className="w-3 h-3" />
      </p>
    </button>
  );
}

function AgentDetailModal({ agentId, agentSnapshot, year, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { url, title }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/reports/agents/${agentId}`, {
          params: { year: year === "all" ? undefined : year },
        });
        if (alive) setData(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [agentId, year]);

  useEffect(() => {
    return () => { if (preview?.url) URL.revokeObjectURL(preview.url); };
  }, [preview]);

  const openPdf = async (kind, item, isDDD) => {
    const itemId = item.id;
    const numar = item.numar;
    try {
      const res = await fetch(`${API_BASE}/${kind}/${itemId}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const raw = await res.blob();
      const blob = new Blob([raw], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const prefix = kind === "contracts" ? (isDDD ? "Contract DDD" : "Contract") : (isDDD ? "PV DDD" : "PV");
      const client = { denumire: item.client_denumire, cui: item.client_cui };
      setPreview({ url, title: `${prefix} Nr. ${numar}`, downloadName: buildDocFilename(kind, client, numar) });
    } catch {
      toast.error("Nu s-a putut deschide documentul");
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const agent = data?.agent || agentSnapshot;

  return (
    <>
      <Dialog open onOpenChange={(v) => { if (!v && !preview) onClose(); }}>
        <DialogContent className="max-w-5xl max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-3">
              <span className={`w-10 h-10 rounded-md flex items-center justify-center ${
                agent?.role === "admin" ? "bg-[hsl(var(--charcoal))] text-[hsl(var(--gold))]" : "bg-gold-soft text-gold-deep"
              }`}>
                {agent?.role === "admin" ? <Shield className="w-5 h-5" /> : <UserCog className="w-5 h-5" />}
              </span>
              {agent?.name}
            </DialogTitle>
            <DialogDescription className="font-mono-crm">{agent?.email}</DialogDescription>
          </DialogHeader>

          {loading || !data ? (
            <p className="text-muted-foreground py-10 text-center">Se încarcă...</p>
          ) : (
            <Tabs defaultValue="clients">
              <TabsList className="bg-white border border-border h-auto p-1">
                <TabsTrigger value="clients" className="data-[state=active]:bg-gold-soft data-[state=active]:text-gold-deep px-3 py-2 text-sm">
                  <Users className="w-4 h-4 mr-2" /> Clienți ({data.clients.length})
                </TabsTrigger>
                <TabsTrigger value="contracts" className="data-[state=active]:bg-gold-soft data-[state=active]:text-gold-deep px-3 py-2 text-sm">
                  <FileText className="w-4 h-4 mr-2" /> Contracte ({data.contracts.length})
                </TabsTrigger>
                <TabsTrigger value="pvs" className="data-[state=active]:bg-gold-soft data-[state=active]:text-gold-deep px-3 py-2 text-sm">
                  <ClipboardCheck className="w-4 h-4 mr-2" /> PV-uri ({data.pvs.length})
                </TabsTrigger>
                <TabsTrigger value="interactions" className="data-[state=active]:bg-gold-soft data-[state=active]:text-gold-deep px-3 py-2 text-sm">
                  <MessageCircle className="w-4 h-4 mr-2" /> Interacțiuni ({data.interactions.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="clients" className="mt-4 space-y-2">
                {data.clients.length === 0 ? (
                  <EmptyList>Niciun client adăugat de acest agent.</EmptyList>
                ) : data.clients.map((c) => (
                  <div key={c.id} className="bg-white border border-border rounded-md p-4 flex items-center justify-between lift-hover">
                    <div className="min-w-0">
                      <p className="label-eyebrow">CUI {c.cui}</p>
                      <p className="font-display font-bold truncate">{c.denumire}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.adresa || c.localitate || "—"}</p>
                    </div>
                    <Link
                      to={`/clienti/${c.id}`}
                      onClick={onClose}
                      className="text-sm text-gold-deep hover:text-gold font-medium shrink-0 ml-4"
                    >
                      Fișă →
                    </Link>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="contracts" className="mt-4 space-y-2">
                {data.contracts.length === 0 ? (
                  <EmptyList>Niciun contract emis de acest agent.</EmptyList>
                ) : data.contracts.map((c) => (
                  <div key={c.id} className="bg-white border border-border rounded-md p-4 flex items-center gap-4 lift-hover">
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-bold">
                        {c.template === "ddd" ? "Contract DDD" : "Contract"} Nr. {c.numar}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {c.client_denumire ? `${c.client_denumire} (CUI ${c.client_cui})` : "—"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(c.data).toLocaleDateString("ro-RO")}
                        {c.valoare ? ` • ${Number(c.valoare).toLocaleString("ro-RO")} ${c.moneda || "RON"}` : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openPdf("contracts", c, c.template === "ddd")}
                      className="border-gold text-gold-deep hover:bg-gold hover:text-[hsl(var(--charcoal))]"
                    >
                      <Eye className="w-4 h-4 mr-1" /> Vezi
                    </Button>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="pvs" className="mt-4 space-y-2">
                {data.pvs.length === 0 ? (
                  <EmptyList>Niciun proces verbal emis de acest agent.</EmptyList>
                ) : data.pvs.map((p) => (
                  <div key={p.id} className="bg-white border border-border rounded-md p-4 flex items-center gap-4 lift-hover">
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-bold">
                        {p.template === "ddd" ? "PV DDD" : "PV"} Nr. {p.numar}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {p.client_denumire ? `${p.client_denumire} (CUI ${p.client_cui})` : p.descriere || "—"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(p.data).toLocaleDateString("ro-RO")}
                        {p.servicii?.length ? ` • ${p.servicii.join(", ")}` : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openPdf("pvs", p, p.template === "ddd")}
                      className="border-gold text-gold-deep hover:bg-gold hover:text-[hsl(var(--charcoal))]"
                    >
                      <Eye className="w-4 h-4 mr-1" /> Vezi
                    </Button>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="interactions" className="mt-4 space-y-2">
                {data.interactions.length === 0 ? (
                  <EmptyList>Nicio interacțiune înregistrată.</EmptyList>
                ) : data.interactions.map((it) => (
                  <div key={it.id} className="bg-white border border-border rounded-md p-4 lift-hover">
                    <div className="flex items-baseline justify-between">
                      <p className="font-medium text-sm capitalize">{it.type.replace("_", " ")}</p>
                      <p className="text-xs text-muted-foreground">{new Date(it.created_at).toLocaleString("ro-RO")}</p>
                    </div>
                    {it.client_denumire && (
                      <p className="text-xs text-muted-foreground mb-1">
                        {it.client_denumire} <span className="font-mono-crm">(CUI {it.client_cui})</span>
                      </p>
                    )}
                    <p className="text-sm mt-1">{it.note}</p>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF preview inside the agent modal */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent className="max-w-5xl h-[85dvh] xl:h-[92dvh] p-0 flex flex-col gap-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
            <div>
              <p className="label-eyebrow">Vizualizare document</p>
              <p className="font-display font-bold text-lg">{preview?.title}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => {
                  if (!preview) return;
                  const a = document.createElement("a");
                  a.href = preview.url;
                  a.download = preview.downloadName;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }}
                className="btn-gold hover:btn-gold"
              >
                <Download className="w-4 h-4 mr-2" /> Descarcă
              </Button>
              <Button size="sm" variant="ghost" onClick={closePreview}>×</Button>
            </div>
          </div>
          <div className="flex-1 bg-slate-200 min-h-0">
            {preview?.url && <PdfViewer file={preview.url} className="h-full" />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmptyList({ children }) {
  return (
    <div className="border border-dashed border-border rounded-md p-8 text-center bg-white text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="border border-border rounded-sm p-2 text-center">
      <Icon className="w-3.5 h-3.5 text-muted-foreground mx-auto" />
      <p className="font-display font-bold text-xl mt-1 text-foreground">{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}
